import { z } from 'zod';

export type JSONValue = string | number | boolean | null | JSONValue[] | { [key: string]: JSONValue };

export const JSONValueSchema: z.ZodType<JSONValue> = z.lazy(() =>
  z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.null(),
    z.array(JSONValueSchema),
    z.record(z.string(), JSONValueSchema),
  ]),
);

// Base operation schema
const BaseOperationSchema = z.object({
  overrideId: z.string().nullable(), // null for base tree
  type: z.union([
    z.literal('insert'),
    z.literal('move'),
    z.literal('trash'),
    z.literal('untrash'),
    z.literal('update'),
    z.literal('updateState'),
    z.literal('removeOverride'),
    z.literal('restoreOverride'),
  ]),
});

// Fractional index as Uint8Array serialized to base64 string
const FractionalIndexSchema = z.string();

// Insert operation schema
export const InsertOperationSchema = BaseOperationSchema.extend({
  type: z.literal('insert'),
  nodeId: z.string(),
  parentId: z.string(),
  fractionalIndex: FractionalIndexSchema,
  isContainer: z.boolean(),
});

// Move operation schema
export const MoveOperationSchema = BaseOperationSchema.extend({
  type: z.literal('move'),
  nodeId: z.string(),
  parentId: z.string(),
  fractionalIndex: FractionalIndexSchema,
});

// Trash operation schema
export const TrashOperationSchema = BaseOperationSchema.extend({
  type: z.literal('trash'),
  nodeId: z.string(),
});

// Untrash operation schema
export const UntrashOperationSchema = BaseOperationSchema.extend({
  type: z.literal('untrash'),
  nodeId: z.string(),
});

// Update operation schema
export const UpdateOperationSchema = BaseOperationSchema.extend({
  type: z.literal('update'),
  nodeId: z.string(),
  target: z.union([z.literal('data'), z.literal('attributes')]),
  key: z.string(),
  value: JSONValueSchema,
});

// Update operation schema
export const UpdateStateOperationSchema = BaseOperationSchema.extend({
  type: z.literal('updateState'),
  key: z.string(),
  value: JSONValueSchema,
});

// RemoveOverride operation schema
export const RemoveOverrideOperationSchema = BaseOperationSchema.extend({
  type: z.literal('removeOverride'),
  nodeId: z.string(),
  target: z.union([z.literal('data'), z.literal('attributes'), z.literal('parents'), z.literal('children')]),
  key: z.string(),
});

// RestoreOverride operation schema
export const RestoreOverrideOperationSchema = BaseOperationSchema.extend({
  type: z.literal('restoreOverride'),
  nodeId: z.string(),
  target: z.union([z.literal('data'), z.literal('attributes'), z.literal('parents'), z.literal('children')]),
  key: z.string(),
  value: JSONValueSchema,
});

// Discriminated union of all operations
export const OperationSchema = z.discriminatedUnion('type', [
  InsertOperationSchema,
  MoveOperationSchema,
  TrashOperationSchema,
  UntrashOperationSchema,
  UpdateOperationSchema,
  UpdateStateOperationSchema,
  RemoveOverrideOperationSchema,
  RestoreOverrideOperationSchema,
]);

// Inferred types
export type InsertOperation = z.infer<typeof InsertOperationSchema>;
export type MoveOperation = z.infer<typeof MoveOperationSchema>;
export type TrashOperation = z.infer<typeof TrashOperationSchema>;
export type UntrashOperation = z.infer<typeof UntrashOperationSchema>;
export type UpdateOperation = z.infer<typeof UpdateOperationSchema>;
export type UpdateStateOperation = z.infer<typeof UpdateStateOperationSchema>;
export type RemoveOverrideOperation = z.infer<typeof RemoveOverrideOperationSchema>;
export type RestoreOverrideOperation = z.infer<typeof RestoreOverrideOperationSchema>;
export type Operation = z.infer<typeof OperationSchema>;

// Validation result schema
export const ValidateOpResultSchema = z
  .object({
    repairedOp: OperationSchema,
    correctiveOp: OperationSchema,
  })
  .nullable();

export type ValidateOpResult = z.infer<typeof ValidateOpResultSchema>;
